import { motion } from 'framer-motion';
import styles from './AIInsightsCard.module.css';

interface AIRecommendations {
    executiveSummary: string;
    priorityActions: Array<{
        priority: 'critical' | 'high' | 'medium' | 'low';
        title: string;
        description: string;
        impact: string;
    }>;
    insights: string[];
    overallAssessment: string;
    generatedAt: string;
}

interface AIInsightsCardProps {
    recommendations: AIRecommendations;
}

const priorityColors = {
    critical: '#dc2626',
    high: '#ef4444',
    medium: '#eab308',
    low: '#22c55e',
};

const priorityLabels = {
    critical: 'Critical',
    high: 'High Priority',
    medium: 'Medium Priority',
    low: 'Low Priority',
};

export function AIInsightsCard({ recommendations }: AIInsightsCardProps) {
    return (
        <motion.div
            className={styles.card}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className={styles.header}>
                <div className={styles.aiIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                    </svg>
                </div>
                <div>
                    <h2 className={styles.title}>AI-Powered Insights</h2>
                    <p className={styles.subtitle}>
                        Powered by Gemini • Generated {new Date(recommendations.generatedAt).toLocaleDateString()}
                    </p>
                </div>
            </div>

            <div className={styles.summary}>
                <h3 className={styles.sectionTitle}>Executive Summary</h3>
                <p className={styles.summaryText}>{recommendations.executiveSummary}</p>
            </div>

            {recommendations.priorityActions.length > 0 && (
                <div className={styles.actions}>
                    <h3 className={styles.sectionTitle}>Priority Actions</h3>
                    <div className={styles.actionsList}>
                        {recommendations.priorityActions.map((action, index) => (
                            <motion.div
                                key={index}
                                className={styles.actionItem}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                            >
                                <div className={styles.actionPriority}>
                                    <span
                                        className={styles.priorityBadge}
                                        style={{ background: priorityColors[action.priority] }}
                                    >
                                        {priorityLabels[action.priority]}
                                    </span>
                                </div>
                                <div className={styles.actionContent}>
                                    <h4 className={styles.actionTitle}>{action.title}</h4>
                                    <p className={styles.actionDescription}>{action.description}</p>
                                    <p className={styles.actionImpact}>
                                        <strong>Impact:</strong> {action.impact}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}

            {recommendations.insights.length > 0 && (
                <div className={styles.insights}>
                    <h3 className={styles.sectionTitle}>Key Insights</h3>
                    <ul className={styles.insightsList}>
                        {recommendations.insights.map((insight, index) => (
                            <motion.li
                                key={index}
                                className={styles.insightItem}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                            >
                                <span className={styles.insightIcon}>•</span>
                                {insight}
                            </motion.li>
                        ))}
                    </ul>
                </div>
            )}

            {recommendations.overallAssessment && (
                <div className={styles.assessment}>
                    <h3 className={styles.sectionTitle}>Overall Assessment</h3>
                    <p className={styles.assessmentText}>{recommendations.overallAssessment}</p>
                </div>
            )}
        </motion.div>
    );
}
